import React from "react";
import { trackAction } from "@/content/site";

/**
 * [Phase 25] Live Google Business Profile reviews module — Testimonials page.
 *
 * The section payload arrives inside the page content (built server-side
 * from the short-lived Google snapshot), so the numbers here always match
 * the AggregateRating JSON-LD emitted for the same page. If the feed is
 * unavailable the section is simply absent — nothing renders.
 */
export default function GoogleReviews({ section, lang }) {
  if (!section || !section.rating || !section.count) return null;

  const ratingLabel =
    lang === "es"
      ? `${section.rating.toFixed(1)} de 5 estrellas`
      : `${section.rating.toFixed(1)} out of 5 stars`;
  const fullStars = Math.round(section.rating);

  const handleGoogleClick = () => {
    trackAction("google_reviews_click", {
      link_domain: "maps.google.com",
      location: "testimonials_page",
      review_count: section.count,
      rating_value: section.rating,
    });
  };

  return (
    <div data-testid="google-reviews-section">
      {section.eyebrow && <span className="gw-eyebrow">{section.eyebrow}</span>}
      {section.heading && <h2 style={{ maxWidth: 640 }}>{section.heading}</h2>}

      <div className="gw-greviews-summary gw-card" data-testid="google-reviews-summary">
        <div className="gw-greviews-score">
          <span className="gw-greviews-number" data-testid="google-rating-value">
            {section.rating.toFixed(1)}
          </span>
          <span
            className="gw-greviews-stars"
            role="img"
            aria-label={ratingLabel}
            data-testid="google-rating-stars"
          >
            {"★".repeat(fullStars)}
            {"☆".repeat(Math.max(0, 5 - fullStars))}
          </span>
        </div>
        <div className="gw-greviews-meta">
          <p className="gw-greviews-summary-text" data-testid="google-review-count">
            {section.summary}
          </p>
          {section.google_maps_uri && (
            <a
              href={section.google_maps_uri}
              target="_blank"
              rel="noopener noreferrer"
              className="gw-btn gw-btn-ghost"
              onClick={handleGoogleClick}
              data-testid="google-reviews-link"
            >
              {section.link_label}
            </a>
          )}
        </div>
      </div>

      {section.items?.length > 0 && (
        <>
          {section.highlight_note && (
            <p className="gw-muted gw-greviews-note" data-testid="google-reviews-note">
              {section.highlight_note}
            </p>
          )}
          <div className="gw-testimonial-grid" data-testid="google-reviews-grid">
            {section.items.map((r, i) => (
              <figure key={`${r.author}-${i}`} className="gw-card gw-testimonial-card" data-testid={`google-review-card-${i}`}>
                <div className="gw-testimonial-stars" aria-label={`${r.rating || 5} stars`}>
                  {"★".repeat(r.rating || 5)}
                </div>
                <blockquote lang={r.text_lang || undefined}>“{r.text}”</blockquote>
                <figcaption>
                  <span className="gw-greviews-author">
                    {r.author_photo && (
                      <img
                        src={r.author_photo}
                        alt=""
                        width="28"
                        height="28"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="gw-greviews-avatar"
                      />
                    )}
                    <strong>{r.author}</strong>
                  </span>
                  <a
                    href={r.review_uri || r.author_uri || section.google_maps_uri}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {r.relative_time ? `${r.relative_time} · ` : ""}via Google
                  </a>
                </figcaption>
              </figure>
            ))}
          </div>
        </>
      )}

      {section.disclaimer && (
        <p className="gw-muted gw-greviews-disclaimer" data-testid="google-reviews-disclaimer">
          {section.disclaimer}
        </p>
      )}

      <style>{`
        .gw-greviews-summary {
          display: flex; align-items: center; flex-wrap: wrap;
          gap: var(--gw-space-6);
          padding: var(--gw-space-6);
          margin-bottom: var(--gw-space-6);
        }
        .gw-greviews-score {
          display: flex; flex-direction: column; align-items: center;
          min-width: 130px;
        }
        .gw-greviews-number {
          font-family: var(--gw-font-serif);
          font-size: 3rem; font-weight: 700; line-height: 1;
          color: var(--gw-primary-dark);
        }
        .gw-greviews-stars {
          color: var(--gw-accent); font-size: 1.3rem; letter-spacing: 3px;
          margin-top: var(--gw-space-2);
        }
        .gw-greviews-meta { flex: 1; min-width: 240px; }
        .gw-greviews-summary-text {
          margin: 0 0 var(--gw-space-3);
          font-size: 1.05rem; line-height: 1.55;
          color: var(--gw-text);
        }
        .gw-greviews-note { font-size: 0.9rem; margin-bottom: var(--gw-space-4); }
        .gw-greviews-disclaimer {
          font-size: 0.82rem; margin-top: var(--gw-space-4);
        }
        .gw-greviews-author { display: inline-flex; align-items: center; gap: 8px; }
        .gw-greviews-avatar { border-radius: 50%; flex-shrink: 0; }
      `}</style>
    </div>
  );
}
